import axios from "axios";
import { getApiUrl, getDomainId, getLocationId, isDevOverride, setDomainId, setLocationId } from "../config/tenant";

const getConfig = () => ({
	headers: {
		Authorization: `Bearer ${localStorage.getItem("token")}`
	}
});

const fetchDomain = async () => {
	if (isDevOverride()) {
		return axios.get(`${getApiUrl()}/domains?domain=${getDomainId()}`);
	}

	const response = await axios.get(`${getApiUrl()}/domains/by-host?host=${window.location.hostname}`);
	setDomainId(response.data._id);
	return response;
}

const fetchDomainAdmin = async () => {
	return axios.get(`${getApiUrl()}/domains/admin`, getConfig());
}

const updateDomain = async (domain) => {
	return axios.put(`${getApiUrl()}/domains`, {
		...domain
	}, getConfig());
}

const fetchEmailNotificationSettings = async () => {
	return axios.get(`${getApiUrl()}/domains/email-notifications`, getConfig());
}

const updateEmailNotificationSettings = async (emailNotifications) => {
	return axios.put(`${getApiUrl()}/domains/email-notifications`, {
		emailNotifications
	}, getConfig());
}

const testRemoteLockConnection = async ({ remoteLockClientId, remoteLockSecret }) => {
	return axios.post(`${getApiUrl()}/domains/remote-lock/test-connection`, {
		remoteLockClientId,
		remoteLockSecret
	}, getConfig());
}

const fetchUser = async () => {
	return axios.get(`${getApiUrl()}/users/me`, getConfig());
}

const fetchUserById = async (id) => {
	return axios.get(`${getApiUrl()}/users/${id}`, getConfig());
}

const updateUser = async ({ firstName, lastName, phoneNumber }) => {
	return axios.put(`${getApiUrl()}/users/me`, {
		firstName, lastName, phoneNumber
	}, getConfig());
}

const createAccount = async ({email, password, firstName, lastName, phoneNumber, subscribe, recaptchaToken}) => {
	return axios.post(`${getApiUrl()}/users/`, {
		Domain: getDomainId(),
		email,
		password,
		firstName,
		lastName,
		phoneNumber,
		subscribe,
		recaptchaToken
	})
}

const login = async (email, password) => {
	return axios.post(`${getApiUrl()}/authentication/login`, {
		Domain: getDomainId(),
		email,
		password
	})
}

const superAdminLogin = async (email, password) => {
	return axios.post(`${getApiUrl()}/authentication/login`, {
		Domain: getDomainId(),
		email,
		password
	});
}

const fetchSuperAdminDomains = async () => {
	return axios.get(`${getApiUrl()}/super-admin/domains`, getConfig());
}

const fetchSuperAdminDomainUsers = async (domainId) => {
	return axios.get(`${getApiUrl()}/super-admin/domains/${domainId}/users`, getConfig());
}

const fetchSuperAdminCustomers = async () => {
	return axios.get(`${getApiUrl()}/super-admin/customers`, getConfig());
}

const fetchSuperAdminCustomerById = async (id) => {
	return axios.get(`${getApiUrl()}/super-admin/customers/${id}`, getConfig());
}

const updateSuperAdminCustomerStatus = async (id, status) => {
	return axios.patch(`${getApiUrl()}/super-admin/customers/${id}/status`, { status }, getConfig());
}

const deleteSuperAdminCustomer = async (id) => {
	return axios.delete(`${getApiUrl()}/super-admin/customers/${id}`, getConfig());
}

const updateSuperAdminDomainDisabled = async (id, disabled) => {
	return axios.patch(`${getApiUrl()}/super-admin/domains/${id}/disabled`, { disabled }, getConfig());
}

const updateSuperAdminDomainPaidExtrasAvailable = async (id, paidExtrasAvailable) => {
	return axios.patch(`${getApiUrl()}/super-admin/domains/${id}/paid-extras-available`, { paidExtrasAvailable }, getConfig());
}

const updateSuperAdminDomain = async (id, updates) => {
	return axios.patch(`${getApiUrl()}/super-admin/domains/${id}`, updates, getConfig());
}

const createSuperAdminDomainUser = async (domainId, payload) => {
	return axios.post(`${getApiUrl()}/super-admin/domains/${domainId}/users`, payload, getConfig());
}

const createOnboardingRequest = async (payload) => {
	return axios.post(`${getApiUrl()}/onboarding-requests`, payload);
}

const verifyEmailAddress = async (pin) => {
	return axios.put(`${getApiUrl()}/users/verify-email-address`, {
		pin
	}, getConfig())
}

const resendEmailVerification = async () => {
	return axios.put(`${getApiUrl()}/users/resend-email-verification`, {}, getConfig());
}

const requestInduction = async (body) => {
	return axios.post(`${getApiUrl()}/users/request-induction`, {
		...body,
		Domain: getDomainId()
	}, getConfig());
}

const acceptTerms = async () => {
	return axios.put(`${getApiUrl()}/users/accept-terms`, {}, getConfig());
}

const completeInduction = async (userId) => {
	return axios.put(`${getApiUrl()}/users/complete-induction`, {
		userId
	}, getConfig());
}

const setUserActivation = async (userId, active) => {
	return axios.put(`${getApiUrl()}/users/${userId}/activation`, {
		active
	}, getConfig());
}

const submitHealthScreening = async (payload) => {
	return axios.put(`${getApiUrl()}/users/complete-health-screening`, payload, getConfig());
}

const fetchHealthScreeningUsers = async () => {
	return axios.get(`${getApiUrl()}/users/health-screening`, getConfig());
}

const clearHealthScreening = async (userId) => {
	return axios.put(`${getApiUrl()}/users/${userId}/clear-health-screening`, {}, getConfig());
}

const fetchHealthScreeningQuestions = async () => {
	return axios.get(`${getApiUrl()}/health-screening-questions`, getConfig());
}

const createHealthScreeningQuestion = async (payload) => {
	return axios.post(`${getApiUrl()}/health-screening-questions`, payload, getConfig());
}

const updateHealthScreeningQuestion = async (id, payload) => {
	return axios.put(`${getApiUrl()}/health-screening-questions/${id}`, payload, getConfig());
}

const moveHealthScreeningQuestion = async (id, direction) => {
	return axios.put(`${getApiUrl()}/health-screening-questions/${id}/move`, { direction }, getConfig());
}

const deleteHealthScreeningQuestion = async (id) => {
	return axios.delete(`${getApiUrl()}/health-screening-questions/${id}`, getConfig());
}

const requestPasswordReset = async (email, recaptchaToken) => {
	return axios.put(`${getApiUrl()}/users/request-password-reset`, {
		Domain: getDomainId(),
		email,
		recaptchaToken
	});
}

const resetPassword = async (email, newPassword, token) => {
	return axios.put(`${getApiUrl()}/users/change-password`, {
		Domain: getDomainId(),
		email,
		newPassword,
		token
	})
}

const changePassword = async ({ Domain, email, oldPassword, newPassword }) => {
	return axios.put(`${getApiUrl()}/users/change-password`, {
		Domain: Domain || getDomainId(),
		email,
		oldPassword,
		newPassword
	});
}

const fetchLocations = async () => {
	return axios.get(`${getApiUrl()}/locations`, getConfig());
}

const resolveLocationId = async () => {
	const existing = getLocationId();
	if (existing) return existing;

	const locations = await fetchLocations();
	const id = locations.data?.[0]?._id;
	if (id) setLocationId(id);
	return id || null;
}

const fetchStudios = async () => {
	const locationId = await resolveLocationId();
	const locationQuery = locationId ? `?location=${locationId}` : '';
	return axios.get(`${getApiUrl()}/studios${locationQuery}`, getConfig());
}

const fetchStudio = async (id) => {
	return axios.get(`${getApiUrl()}/studios/${id}`, getConfig());
}

const fetchStudioByShareLink = async (shareLink) => {
	return axios.get(`${getApiUrl()}/bookings/join/${shareLink}`, getConfig());
}

const updateStudio = async (studio) => {
	return axios.put(`${getApiUrl()}/studios/${studio._id}`, {
		...studio
	}, getConfig());
}

const createStudio = async (studio) => {
	const location = studio.Location || await resolveLocationId();

	return axios.post(`${getApiUrl()}/studios`, {
		...studio,
		Location: location
	}, getConfig());
}

const fetchSessions = async (studio, start, end) => {
	return axios.get(`${getApiUrl()}/studios/${studio}/sessions?start=${start}&end=${end}`, getConfig());
}

const fetchSession = async (studio, start, end, shareLink) => {
	return await axios.get(`${getApiUrl()}/studios/${studio}/session?start=${start}&end=${end}&shareLink=${shareLink}`, getConfig());
}

const createBooking = async (session, shareLink) => {
	return axios.post(`${getApiUrl()}/bookings`, { session, shareLink }, getConfig());
}

const createBulkBookings = async (sessions) => {
	return axios.post(`${getApiUrl()}/bookings/bulk`, { sessions }, getConfig());
}

const createUserBooking = async (booking) => {
	return axios.post(`${getApiUrl()}/bookings`, { ...booking }, getConfig());
}

const fetchBookings = async (start, end, all = false) => {
	const params = new URLSearchParams();
	if (all) {
		params.set('all', 'true');
	} else {
		if (start) params.set('start', start);
		if (end) params.set('end', end);
	}
	const query = params.toString();
	return axios.get(`${getApiUrl()}/bookings${query ? `?${query}` : ''}`, getConfig());
}

const fetchBooking = async (id) => {
	return axios.get(`${getApiUrl()}/bookings/${id}`, getConfig());
}

const cancelBooking = async (id) => {
	return axios.put(`${getApiUrl()}/bookings/${id}/cancel`, {}, getConfig());
}

const joinWaitlist = async (payload) => {
	return axios.post(`${getApiUrl()}/waitlist`, payload, getConfig());
}

const leaveWaitlist = async (payload) => {
	return axios.delete(`${getApiUrl()}/waitlist`, {
		...getConfig(),
		data: payload
	});
}

const fetchWaitlist = async () => {
	return axios.get(`${getApiUrl()}/waitlist`, getConfig());
}

const fetchProducts = async () => {
	return axios.get(`${getApiUrl()}/products/admin`, getConfig());
}

const fetchArchivedProducts = async () => {
	return axios.get(`${getApiUrl()}/products/admin/archived`, getConfig());
}

const fetchPublicProducts = async () => {
	return axios.get(`${getApiUrl()}/products/${getDomainId()}`);
}

const fetchProduct = async (id) => {
	return axios.get(`${getApiUrl()}/products/admin/${id}`, getConfig());
}

const fetchPublicProduct = async (id) => {
	return axios.get(`${getApiUrl()}/products/${getDomainId()}/${id}`);
}

const updateProduct = async (product) => {
	return axios.put(`${getApiUrl()}/products/admin/${product._id}`, {
		...product
	}, getConfig());
}

const createProduct = async (product) => {
	return axios.post(`${getApiUrl()}/products/admin`, {
		...product
	}, getConfig());
}

const fetchWalletSummary = async () => {
	return axios.get(`${getApiUrl()}/wallet`, getConfig());
}

const fetchWalletTransactions = async () => {
	return axios.get(`${getApiUrl()}/wallet/transactions`, getConfig());
}

const exportWalletTransactions = async () => {
	return axios.get(`${getApiUrl()}/wallet/transactions/export`, {
		...getConfig(),
		responseType: 'blob'
	});
}

const createWalletCheckout = async (walletPackageId, returnPath, marketingConsent = false) => {
	return axios.post(`${getApiUrl()}/wallet/create-checkout`, {
		walletPackageId,
		returnPath,
		marketingConsent
	}, getConfig());
}

const verifyWalletWorldpayPayment = async (transactionId) => {
	return axios.post(`${getApiUrl()}/wallet/verify-worldpay`, {
		transactionId
	}, getConfig());
}

const verifyWalletSumupPayment = async (transactionId, checkoutId) => {
	return axios.post(`${getApiUrl()}/wallet/verify-payment`, {
		transactionId,
		checkoutId
	}, getConfig());
}

const createWalletAdjustment = async ({ userId, amount, note, tokenType }) => {
	return axios.post(`${getApiUrl()}/wallet/adjustment`, {
		userId,
		amount,
		tokenType,
		note
	}, getConfig());
}

const fetchPublicWalletPackages = async () => {
	return axios.get(`${getApiUrl()}/wallet-packages/${getDomainId()}`);
}

const fetchWalletPackages = async () => {
	return axios.get(`${getApiUrl()}/wallet-packages/admin`, getConfig());
}

const fetchWalletPackage = async (id) => {
	return axios.get(`${getApiUrl()}/wallet-packages/admin/${id}`, getConfig());
}

const createWalletPackage = async (walletPackage) => {
	return axios.post(`${getApiUrl()}/wallet-packages/admin`, walletPackage, getConfig());
}

const updateWalletPackage = async (walletPackage) => {
	return axios.put(`${getApiUrl()}/wallet-packages/admin/${walletPackage._id}`, walletPackage, getConfig());
}

const createPaypalOrder = async (product, discountCode) => {
	return axios.post(`${getApiUrl()}/transactions`, {
		product,
		discountCode
	}, getConfig());
}

const createWorldpayCheckout = async (product, discountCode) => {
	return axios.post(`${getApiUrl()}/worldpay/create-checkout`, {
		product,
		discountCode
	}, getConfig());
}

const createStripeCheckout = async (product, discountCode) => {
	return axios.post(`${getApiUrl()}/transactions/stripe/create-checkout`, {
		product,
		discountCode
	}, getConfig());
}

const createSumupCheckout = async (product, discountCode) => {
	return axios.post(`${getApiUrl()}/sumup/create-checkout`, {
		product,
		discountCode,
		returnUrl: window.location.origin
	}, getConfig());
}

const createPaypalGiftVoucherOrder = async (product, discountCode) => {
	return axios.post(`${getApiUrl()}/transactions/gift-vouchers`, {
		Domain: getDomainId(),
		product,
		discountCode
	}, getConfig());
}

const approvePaypalOrder = async (orderId) => {
	return axios.post(`${getApiUrl()}/transactions/${orderId}/capture`, {
		Domain: getDomainId(),
	}, getConfig());
}

const approvePaypalGiftVoucherOrder = async (orderId) => {
	return axios.post(`${getApiUrl()}/transactions/gift-vouchers/${orderId}/capture`, {
		Domain: getDomainId(),
	}, getConfig());
}

const createWorldpayGiftVoucherCheckout = async (productId, discountCode, purchaser) => {
    return axios.post(`${getApiUrl()}/worldpay/gift-vouchers/create-checkout`, {
        product: productId,
        discountCode,
        purchaser,
        Domain: getDomainId(),
    }, getConfig());
}

const fetchTransactions = async () => {
	return axios.get(`${getApiUrl()}/transactions`, getConfig());
}

const fetchTransaction = async (id) => {
	return axios.get(`${getApiUrl()}/transactions/${id}`, getConfig());
}

const createTransaction = async (transaction) => {
	return axios.post(`${getApiUrl()}/transactions/adjustment`, {
		...transaction
	}, getConfig());
}

const fetchMinors = async () => {
	return axios.get(`${getApiUrl()}/minors`, getConfig());
}

const fetchMinorById = async (id) => {
	return axios.get(`${getApiUrl()}/minors/${id}`, getConfig());
}

const createMinor = async (minor) => {
	return axios.post(`${getApiUrl()}/minors`, minor, getConfig());
}

const fetchUsers = async () => {
	return axios.get(`${getApiUrl()}/users`, getConfig());
}

const fetchUsersPage = async ({ page, pageSize, search }) => {
	const params = new URLSearchParams({ page, pageSize });
	if (search) params.set('search', search);
	return axios.get(`${getApiUrl()}/users?${params.toString()}`, getConfig());
}

const exportUsers = async () => {
    return axios.get(`${getApiUrl()}/users/export`, {
        ...getConfig(),
        responseType: 'blob'
    });
}

const exportUserImportTemplate = async () => {
    return axios.get(`${getApiUrl()}/users/import-template`, {
        ...getConfig(),
        responseType: 'blob'
    });
}

const importUsers = async (file, onUploadProgress, onImportProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    let downloadedLength = 0;
    let progressBuffer = '';
    return axios.post(`${getApiUrl()}/users/import?stream=true`, formData, {
        ...getConfig(),
        responseType: 'text',
        headers: {
            ...getConfig().headers,
            'Content-Type': 'multipart/form-data'
        },
        onUploadProgress,
        onDownloadProgress: (progressEvent) => {
            if(!onImportProgress) return;
            const responseText = progressEvent.currentTarget?.response || progressEvent.target?.responseText || '';
            const nextChunk = responseText.slice(downloadedLength);
            downloadedLength = responseText.length;
            progressBuffer += nextChunk;
            const lines = progressBuffer.split('\n');
            progressBuffer = lines.pop();
            lines.filter(Boolean).forEach((line) => {
                try {
                    onImportProgress(JSON.parse(line));
                } catch(err) {
                    console.error('Failed to parse import progress', err);
                }
            });
        }
    });
}

const exportMembers = async () => {
    return axios.get(`${getApiUrl()}/users/export-members`, {
        ...getConfig(),
        responseType: 'blob'
    });
}

const fetchDiscountCodes = async () => {
	return axios.get(`${getApiUrl()}/discount-codes`, getConfig());
}

const fetchDiscountCode = async (id) => {
	return axios.get(`${getApiUrl()}/discount-codes/${id}`, getConfig());
}

const updateDiscountCode = async (discountCode) => {
	return axios.put(`${getApiUrl()}/discount-codes/${discountCode._id}`, {
		...discountCode
	}, getConfig());
}

const createDiscountCode = async (discountCode) => {
	return axios.post(`${getApiUrl()}/discount-codes`, {
		...discountCode
	}, getConfig());
}

const checkDiscountCode = async (code) => {
	return axios.get(`${getApiUrl()}/discount-codes/code/${code}`, getConfig());
}

const getMarketingStatus = async () => {
	return axios.get(`${getApiUrl()}/marketing`, getConfig());
}

const subscribe = async () => {
	return axios.put(`${getApiUrl()}/marketing/subscribe`, {}, getConfig());
}

const unsubscribe = async () => {
	return axios.put(`${getApiUrl()}/marketing/unsubscribe`, {}, getConfig());
}

const resubscribe = async () => {
	return axios.put(`${getApiUrl()}/marketing/resubscribe`, {}, getConfig());
}

const sendIssueReport = async ({ category, report }) => {
	return axios.post(`${getApiUrl()}/issue`, {
		category,
		report
	}, getConfig());
}

const redeemGiftVoucher = async (voucherCode) => {
	return axios.post(`${getApiUrl()}/transactions/gift-vouchers/redeem`, { voucherCode }, getConfig())
}

const subscribeToNotifications = async token => {
	return axios.post(`${getApiUrl()}/notifications/subscribe`, { token }, getConfig())
}

const deleteAccount = async () => {
	return axios.delete(`${getApiUrl()}/users/me`, getConfig());
}

const fetchMembershipPlans = async () => {
	return axios.get(`${getApiUrl()}/membership-plans`, getConfig());
}

const fetchMembershipPlan = async (id) => {
	return axios.get(`${getApiUrl()}/membership-plans/${id}`, getConfig());
}

const fetchPaidExtrasPlan = async () => {
	return axios.get(`${getApiUrl()}/membership-plans/paid-extras`, getConfig());
}

const fetchMembershipTransactions = async () => {
	return axios.get(`${getApiUrl()}/membership-transactions`, getConfig());
}

const exportMembershipTransactions = async () => {
	return axios.get(`${getApiUrl()}/membership-transactions/export`, {
		...getConfig(),
		responseType: 'blob'
	});
}

const processRecurringPayments = async () => {
	return axios.post(`${getApiUrl()}/membership-transactions/process-recurring`, {}, getConfig());
}

const fetchBillingStatus = async () => {
	return axios.get(`${getApiUrl()}/membership-transactions/billing-status`, getConfig());
}

const fetchMembershipPlanSubscriberCount = async (id) => {
	return axios.get(`${getApiUrl()}/membership-plans/${id}/subscriber-count`, getConfig());
}

const applyPriceToSubscribers = async (id) => {
	return axios.put(`${getApiUrl()}/membership-plans/${id}/apply-price`, {}, getConfig());
}

const createMembershipPlan = async ({name, description, amount, active, interval_count, interval, lockstateLockId, secondaryLockstateLockId, startTime, endTime, paymentType, maxPayments, subscriptionEndDate, tokensPerCycle, openGymTokensPerCycle, classTokensPerCycle, tokenExpiryDays, isPaidExtrasPlan}) => {
	return axios.post(`${getApiUrl()}/membership-plans`, {
		name,
		description,
		amount,
		active,
		interval_count,
		interval,
		lockstateLockId,
		secondaryLockstateLockId,
		startTime,
		endTime,
		paymentType,
		maxPayments,
		subscriptionEndDate,
		tokensPerCycle,
		openGymTokensPerCycle,
		classTokensPerCycle,
		tokenExpiryDays,
		isPaidExtrasPlan
	}, getConfig());
}

const updateMembershipPlan = async ({_id, name, description, amount, active, interval_count, interval, lockstateLockId, secondaryLockstateLockId, startTime, endTime, scheduleStartTime, scheduleEndTime, paymentType, maxPayments, subscriptionEndDate, tokensPerCycle, openGymTokensPerCycle, classTokensPerCycle, tokenExpiryDays}) => {
	return axios.put(`${getApiUrl()}/membership-plans/${_id}`, {
		name,
		description,
		amount,
		active,
		interval_count,
		interval,
		lockstateLockId,
		secondaryLockstateLockId,
		startTime,
		endTime,
		scheduleStartTime,
		scheduleEndTime,
		paymentType,
		maxPayments,
		subscriptionEndDate,
		tokensPerCycle,
		openGymTokensPerCycle,
		classTokensPerCycle,
		tokenExpiryDays
	}, getConfig());
}

const subscribeToMembershipPlan = async (membershipPlanId, paymentType = 'subscription', discountCode = null) => {
	return axios.post(`${getApiUrl()}/memberships`, {
		membershipPlanId,
		paymentType,
		discountCode
	}, getConfig())
}

const fetchMemberships = async () => {
	return axios.get(`${getApiUrl()}/memberships`, getConfig());
}

const fetchBillingPortalSession = async (flowType) => {
	return axios.post(`${getApiUrl()}/memberships/billing-portal`, {
		flowType
	}, getConfig());
}

const updatePersonalAccessCode = async (personalAccessCode) => {
	return axios.put(`${getApiUrl()}/users/personal-access-code`, {
		personalAccessCode
	}, getConfig());
}

const fetchCoupons = async () => {
	return axios.get(`${getApiUrl()}/coupons`, getConfig());
}

const fetchCoupon = async id => {
	return axios.get(`${getApiUrl()}/coupons/${id}`, getConfig());
}

const createCoupon = async ({name, percent_off, code}) => {
	return axios.post(`${getApiUrl()}/coupons`, {
		name,
		percent_off,
		code
	}, getConfig());
}

const deleteCoupon = async id => {
	return axios.delete(`${getApiUrl()}/coupons/${id}`, getConfig());
}

// Worldpay Subscriptions
const fetchWorldpaySubscriptions = async () => {
	return axios.get(`${getApiUrl()}/worldpay-subscriptions`, getConfig());
}

const fetchWorldpaySubscription = async (id) => {
	return axios.get(`${getApiUrl()}/worldpay-subscriptions/${id}`, getConfig());
}

const fetchWorldpaySubscriptionPayments = async (id) => {
	return axios.get(`${getApiUrl()}/worldpay-subscriptions/${id}/payments`, getConfig());
}

const createWorldpayMembershipSubscription = async (membershipPlanId, paymentType = 'subscription', discountCode = null) => {
	return axios.post(`${getApiUrl()}/worldpay-subscriptions/membership`, {
		membershipPlanId,
		paymentType,
		discountCode
	}, getConfig());
}

const createWorldpayCreditsSubscription = async (productId, interval, intervalCount) => {
	return axios.post(`${getApiUrl()}/worldpay-subscriptions/credits`, {
		productId,
		interval,
		intervalCount
	}, getConfig());
}

const cancelWorldpaySubscription = async (subscriptionId, immediate = false) => {
	return axios.put(`${getApiUrl()}/worldpay-subscriptions/${subscriptionId}/cancel`, {
		immediate
	}, getConfig());
}

const pauseWorldpaySubscription = async (subscriptionId) => {
	return axios.put(`${getApiUrl()}/worldpay-subscriptions/${subscriptionId}/pause`, {}, getConfig());
}

const resumeWorldpaySubscription = async (subscriptionId) => {
	return axios.put(`${getApiUrl()}/worldpay-subscriptions/${subscriptionId}/resume`, {}, getConfig());
}

// SumUp Subscriptions
const fetchSumupSubscriptions = async () => {
	return axios.get(`${getApiUrl()}/sumup-subscriptions`, getConfig());
}

const fetchSumupSubscription = async (id) => {
	return axios.get(`${getApiUrl()}/sumup-subscriptions/${id}`, getConfig());
}

const createSumupMembershipSubscription = async (membershipPlanId, paymentType = 'subscription', discountCode = null) => {
	return axios.post(`${getApiUrl()}/sumup-subscriptions/membership`, {
		membershipPlanId,
		paymentType,
		returnUrl: window.location.origin,
		discountCode
	}, getConfig());
}

const createSumupCreditsSubscription = async (productId, interval, intervalCount) => {
	return axios.post(`${getApiUrl()}/sumup-subscriptions/credits`, {
		productId,
		interval,
		intervalCount,
		returnUrl: window.location.origin
	}, getConfig());
}

const cancelSumupSubscription = async (subscriptionId, immediate = false) => {
	return axios.put(`${getApiUrl()}/sumup-subscriptions/${subscriptionId}/cancel`, {
		immediate
	}, getConfig());
}

const pauseSumupSubscription = async (subscriptionId) => {
	return axios.put(`${getApiUrl()}/sumup-subscriptions/${subscriptionId}/pause`, {}, getConfig());
}

const resumeSumupSubscription = async (subscriptionId) => {
	return axios.put(`${getApiUrl()}/sumup-subscriptions/${subscriptionId}/resume`, {}, getConfig());
}

// Verify Worldpay payment (for when webhooks don't work)
const verifyWorldpayPayment = async (transactionId) => {
	return axios.post(`${getApiUrl()}/worldpay/verify-payment`, {
		transactionId
	}, getConfig());
}

// Verify SumUp payment
const verifySumupPayment = async (transactionId, checkoutId) => {
	return axios.post(`${getApiUrl()}/sumup/verify-payment`, {
		transactionId,
		checkoutId
	}, getConfig());
}

// Verify Worldpay subscription (for when webhooks don't work)
const verifyWorldpaySubscription = async (setupId) => {
	return axios.post(`${getApiUrl()}/worldpay-subscriptions/verify`, {
		setupId
	}, getConfig());
}

// Admin: verify/refresh a subscription for a specific user
const adminVerifyWorldpaySubscription = async (userId, setupId) => {
	return axios.post(`${getApiUrl()}/worldpay-subscriptions/admin/verify`, { userId, setupId }, getConfig());
}

// Admin: force-retry a payment for a specific subscription
const adminForceSubscriptionPayment = async (subscriptionId) => {
	return axios.post(`${getApiUrl()}/worldpay-subscriptions/${subscriptionId}/force-payment`, {}, getConfig());
}

// Admin: refresh Worldpay token for a subscription using its original setupId
const adminRefreshWorldpayToken = async (subscriptionId, setupId) => {
	return axios.post(`${getApiUrl()}/worldpay-subscriptions/admin/refresh-token`, { subscriptionId, setupId }, getConfig());
}

// Admin: verify/refresh a SumUp subscription for a specific user
const adminVerifySumupSubscription = async (userId, setupId) => {
	return axios.post(`${getApiUrl()}/sumup-subscriptions/admin/verify`, { userId, setupId }, getConfig());
}

// Initiate a Worldpay payment method update for an existing subscription
const initiateWorldpayPaymentUpdate = async (subscriptionId) => {
	return axios.post(`${getApiUrl()}/worldpay-subscriptions/initiate-update`, { subscriptionId }, getConfig());
}

// Verify a Worldpay payment method update after returning from Worldpay
const verifyWorldpayPaymentUpdate = async (updateId) => {
	return axios.post(`${getApiUrl()}/worldpay-subscriptions/verify-update`, { updateId }, getConfig());
}

// Admin: get pending wallet transactions for a user
const adminFetchPendingWalletTransactions = async (userId) => {
	return axios.get(`${getApiUrl()}/wallet/admin/pending-transactions/${userId}`, getConfig());
}

// Admin: complete a pending wallet transaction
const adminCompleteWalletTransaction = async (transactionId) => {
	return axios.post(`${getApiUrl()}/wallet/admin/complete-transaction`, { transactionId }, getConfig());
}

// Admin: get all pending wallet transactions across the domain
const adminFetchAllPendingWalletTransactions = async () => {
	return axios.get(`${getApiUrl()}/wallet/admin/pending-transactions`, getConfig());
}

// Admin: get full wallet transaction history for a user
const adminFetchUserWalletTransactions = async (userId) => {
	return axios.get(`${getApiUrl()}/wallet/admin/transactions/${userId}`, getConfig());
}

// Verify SumUp subscription
const verifySumupSubscription = async (setupId) => {
	return axios.post(`${getApiUrl()}/sumup-subscriptions/verify`, {
		setupId
	}, getConfig());
}
// ============ SERVICES ============

const fetchServices = async () => {
	return axios.get(`${getApiUrl()}/services`, getConfig());
}

const fetchService = async (id) => {
	return axios.get(`${getApiUrl()}/services/${id}`, getConfig());
}

const fetchServiceBySlug = async (slug) => {
	return axios.get(`${getApiUrl()}/services/slug/${slug}`, getConfig());
}

const fetchServiceCategories = async () => {
	return axios.get(`${getApiUrl()}/services/categories`, getConfig());
}

const fetchServiceAvailability = async (serviceId, start, end, staffId = null) => {
	let url = `${getApiUrl()}/services/${serviceId}/availability?start=${start}&end=${end}`;
	if (staffId) url += `&staffId=${staffId}`;
	return axios.get(url, getConfig());
}

const fetchServiceAvailabilityWithUnavailable = async (serviceId, start, end, staffId = null) => {
	let url = `${getApiUrl()}/services/${serviceId}/availability?start=${start}&end=${end}&includeUnavailable=true`;
	if (staffId) url += `&staffId=${staffId}`;
	return axios.get(url, getConfig());
}

const fetchServiceStaff = async (serviceId) => {
	return axios.get(`${getApiUrl()}/services/${serviceId}/staff`, getConfig());
}

const createService = async (service) => {
	return axios.post(`${getApiUrl()}/services`, service, getConfig());
}

const updateService = async (id, service) => {
	return axios.put(`${getApiUrl()}/services/${id}`, service, getConfig());
}

const deleteService = async (id) => {
	return axios.delete(`${getApiUrl()}/services/${id}`, getConfig());
}

const publishService = async (id) => {
	return axios.put(`${getApiUrl()}/services/${id}/publish`, {}, getConfig());
}

const unpublishService = async (id) => {
	return axios.put(`${getApiUrl()}/services/${id}/unpublish`, {}, getConfig());
}

const exportServices = async () => {
	return axios.get(`${getApiUrl()}/services/export`, {
		...getConfig(),
		responseType: 'blob'
	});
}

const exportServiceImportTemplate = async () => {
	return axios.get(`${getApiUrl()}/services/import-template`, {
		...getConfig(),
		responseType: 'blob'
	});
}

const importServices = (file, onUploadProgress, onImportProgress) => {
	const formData = new FormData();
	formData.append('file', file);
	let downloadedLength = 0;
	let progressBuffer = '';

	return axios.post(`${getApiUrl()}/services/import?stream=true`, formData, {
		...getConfig(),
		responseType: 'text',
		headers: {
			...getConfig().headers,
			'Content-Type': 'multipart/form-data'
		},
		onUploadProgress,
		onDownloadProgress: (progressEvent) => {
			if (!onImportProgress) return;
			const responseText = progressEvent.currentTarget?.response || progressEvent.target?.responseText || '';
			const nextChunk = responseText.slice(downloadedLength);
			downloadedLength = responseText.length;
			progressBuffer += nextChunk;
			const lines = progressBuffer.split('\n');
			progressBuffer = lines.pop();
			lines.filter(Boolean).forEach((line) => {
				try {
					onImportProgress(JSON.parse(line));
				} catch (err) {
					console.error('Failed to parse service import progress', err);
				}
			});
		}
	});
}

// ============ STAFF ============

const fetchStaff = async () => {
	return axios.get(`${getApiUrl()}/staff`, getConfig());
}

const fetchStaffMember = async (id) => {
	return axios.get(`${getApiUrl()}/staff/${id}`, getConfig());
}

const createStaffMember = async (staff) => {
	return axios.post(`${getApiUrl()}/staff`, staff, getConfig());
}

const updateStaffMember = async (id, staff) => {
	return axios.put(`${getApiUrl()}/staff/${id}`, staff, getConfig());
}

const deleteStaffMember = async (id) => {
	return axios.delete(`${getApiUrl()}/staff/${id}`, getConfig());
}

const setStaffServices = async (staffId, serviceIds) => {
	return axios.put(`${getApiUrl()}/staff/${staffId}/services`, { serviceIds }, getConfig());
}

const buildStaffReportQuery = ({ start, end, status, paymentStatus } = {}) => {
	const params = new URLSearchParams();
	if (start) params.set('start', start);
	if (end) params.set('end', end);
	if (status) params.set('status', status);
	if (paymentStatus) params.set('paymentStatus', paymentStatus);
	return params.toString();
}

const fetchStaffReport = async (staffId, filters) => {
	return axios.get(`${getApiUrl()}/staff/${staffId}/report?${buildStaffReportQuery(filters)}`, getConfig());
}

const exportStaffReport = async (staffId, filters) => {
	const query = buildStaffReportQuery(filters);
	return axios.get(`${getApiUrl()}/staff/${staffId}/report?${query}${query ? '&' : ''}format=csv`, {
		...getConfig(),
		responseType: 'blob'
	});
}

// ============ STAFF AVAILABILITY ============

const fetchStaffAvailability = async (staffId, start, end) => {
	return axios.get(`${getApiUrl()}/staff-availability/${staffId}?start=${start}&end=${end}`, getConfig());
}

const setStaffAvailability = async (staffId, date, windows) => {
	return axios.post(`${getApiUrl()}/staff-availability/${staffId}`, { date, windows }, getConfig());
}

const setBulkStaffAvailability = async (staffId, dates, windows) => {
	return axios.post(`${getApiUrl()}/staff-availability/${staffId}/bulk`, { dates, windows }, getConfig());
}

const blockStaffDate = async (staffId, date, reason) => {
	return axios.post(`${getApiUrl()}/staff-availability/${staffId}/block`, { date, reason }, getConfig());
}

const unblockStaffDate = async (staffId, date) => {
	return axios.post(`${getApiUrl()}/staff-availability/${staffId}/unblock`, { date }, getConfig());
}

// ============ SERVICE BOOKINGS ============

const fetchServiceBookings = async (start, end) => {
	return axios.get(`${getApiUrl()}/service-bookings?start=${start}&end=${end}`, getConfig());
}

const fetchMyServiceBookings = async () => {
	return axios.get(`${getApiUrl()}/service-bookings/my`, getConfig());
}

const fetchServiceBooking = async (id) => {
	return axios.get(`${getApiUrl()}/service-bookings/${id}`, getConfig());
}

const createServiceBooking = async ({ serviceId, staffId, date, startTime, customerNotes, paymentMethod }) => {
	return axios.post(`${getApiUrl()}/service-bookings`, {
		serviceId,
		staffId,
		date,
		startTime,
		customerNotes,
		paymentMethod,
		returnUrl: window.location.origin
	}, getConfig());
}

const createBulkServiceBookings = async ({ bookings, paymentMethod, discountCode }) => {
	return axios.post(`${getApiUrl()}/service-bookings/bulk`, {
		bookings,
		paymentMethod,
		discountCode,
		returnUrl: window.location.origin
	}, getConfig());
}

const verifySumupServiceBooking = async (bookingId) => {
	return axios.post(`${getApiUrl()}/service-bookings/verify-payment`, {
		bookingId
	}, getConfig());
}

const verifySumupBulkServiceBookings = async (checkoutId) => {
	return axios.post(`${getApiUrl()}/service-bookings/verify-bulk-payment`, {
		checkoutId
	}, getConfig());
}

const cancelServiceBooking = async (id, reason) => {
	return axios.put(`${getApiUrl()}/service-bookings/${id}/cancel`, { reason }, getConfig());
}

const uploadImage = async (formData) => {
	return axios.post(`${getApiUrl()}/images`, formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
			...getConfig().headers
		}
	});
}

const updateServiceBookingStatus = async (id, status) => {
	return axios.put(`${getApiUrl()}/service-bookings/${id}/status`, { status }, getConfig());
}

export default {
	fetchDomain,
	fetchDomainAdmin,
	updateDomain,
	fetchEmailNotificationSettings,
	updateEmailNotificationSettings,
	testRemoteLockConnection,
	fetchUser,
	fetchUserById,
	updateUser,
	createAccount,
	login,
	superAdminLogin,
	fetchSuperAdminDomains,
	fetchSuperAdminDomainUsers,
	fetchSuperAdminCustomers,
	fetchSuperAdminCustomerById,
	updateSuperAdminCustomerStatus,
	deleteSuperAdminCustomer,
	updateSuperAdminDomainDisabled,
	updateSuperAdminDomainPaidExtrasAvailable,
	updateSuperAdminDomain,
	createSuperAdminDomainUser,
	createOnboardingRequest,
	verifyEmailAddress,
	resendEmailVerification,
	requestInduction,
	acceptTerms,
	completeInduction,
	setUserActivation,
	submitHealthScreening,
	fetchHealthScreeningUsers,
	clearHealthScreening,
	fetchHealthScreeningQuestions,
	createHealthScreeningQuestion,
	updateHealthScreeningQuestion,
	moveHealthScreeningQuestion,
	deleteHealthScreeningQuestion,
	requestPasswordReset,
	resetPassword,
	changePassword,
	fetchLocations,
	resolveLocationId,
	fetchStudios,
	fetchStudio,
	fetchStudioByShareLink,
	updateStudio,
	createStudio,
	fetchSessions,
	fetchSession,
	createBooking,
	createBulkBookings,
	createUserBooking,
	fetchBookings,
	fetchBooking,
	cancelBooking,
	joinWaitlist,
	leaveWaitlist,
	fetchWaitlist,
	fetchProducts,
	fetchArchivedProducts,
	fetchProduct,
	fetchWalletSummary,
	fetchWalletTransactions,
	exportWalletTransactions,
	createWalletCheckout,
	verifyWalletWorldpayPayment,
	verifyWalletSumupPayment,
	createWalletAdjustment,
	fetchPublicWalletPackages,
	fetchWalletPackages,
	fetchWalletPackage,
	createWalletPackage,
	updateWalletPackage,
	createWorldpayCheckout,
	createStripeCheckout,
	createSumupCheckout,
	createPaypalOrder,
	approvePaypalOrder,
	fetchTransactions,
	fetchTransaction,
	createTransaction,
	fetchMinors,
	fetchMinorById,
	createMinor,
	getMarketingStatus,
	subscribe,
	unsubscribe,
	resubscribe,
	sendIssueReport,
	createPaypalGiftVoucherOrder,
	approvePaypalGiftVoucherOrder,
	subscribeToNotifications,
	fetchMembershipTransactions,
	exportMembershipTransactions,
	processRecurringPayments,
	fetchBillingStatus,
	fetchMembershipPlans,
	fetchMembershipPlan,
	fetchPaidExtrasPlan,
	fetchMembershipPlanSubscriberCount,
	applyPriceToSubscribers,
	createMembershipPlan,
	updateMembershipPlan,
	subscribeToMembershipPlan,
	fetchMemberships,
	fetchBillingPortalSession,
	updatePersonalAccessCode,

	// ADMIN
	updateProduct,
	createProduct,

	fetchUsers,
	fetchUsersPage,
    exportUsers,
    exportUserImportTemplate,
    importUsers,
    exportMembers,

	fetchDiscountCodes,
	fetchDiscountCode,
	updateDiscountCode,
	createDiscountCode,
	checkDiscountCode,
	redeemGiftVoucher,
	fetchPublicProducts,
	fetchPublicProduct,
	deleteAccount,
	fetchCoupons,
	fetchCoupon,
	createCoupon,
	deleteCoupon,
	createWorldpayGiftVoucherCheckout,

	// Worldpay Subscriptions
	fetchWorldpaySubscriptions,
	fetchWorldpaySubscription,
	fetchWorldpaySubscriptionPayments,
	createWorldpayMembershipSubscription,
	createWorldpayCreditsSubscription,
	cancelWorldpaySubscription,
	pauseWorldpaySubscription,
	resumeWorldpaySubscription,
	verifyWorldpayPayment,
	verifyWorldpaySubscription,
	adminVerifyWorldpaySubscription,
	adminForceSubscriptionPayment,
	adminRefreshWorldpayToken,
	initiateWorldpayPaymentUpdate,
	verifyWorldpayPaymentUpdate,
	adminFetchPendingWalletTransactions,
	adminCompleteWalletTransaction,
	adminFetchAllPendingWalletTransactions,
	adminFetchUserWalletTransactions,
	adminVerifySumupSubscription,
	// SumUp Subscriptions
	fetchSumupSubscriptions,
	fetchSumupSubscription,
	createSumupMembershipSubscription,
	createSumupCreditsSubscription,
	cancelSumupSubscription,
	pauseSumupSubscription,
	resumeSumupSubscription,
	verifySumupPayment,
	verifySumupSubscription,
	// Services
	fetchServices,
	fetchService,
	fetchServiceBySlug,
	fetchServiceCategories,
	fetchServiceAvailability,
	fetchServiceAvailabilityWithUnavailable,
	fetchServiceStaff,
	createService,
	updateService,
	deleteService,
	publishService,
	unpublishService,
	exportServices,
	exportServiceImportTemplate,
	importServices,

	// Staff
	fetchStaff,
	fetchStaffMember,
	createStaffMember,
	updateStaffMember,
	deleteStaffMember,
	setStaffServices,
	fetchStaffReport,
	exportStaffReport,

	// Staff Availability
	fetchStaffAvailability,
	setStaffAvailability,
	setBulkStaffAvailability,
	blockStaffDate,
	unblockStaffDate,

	// Service Bookings
	fetchServiceBookings,
	fetchMyServiceBookings,
	fetchServiceBooking,
	createServiceBooking,
	createBulkServiceBookings,
	verifySumupServiceBooking,
	verifySumupBulkServiceBookings,
	cancelServiceBooking,
	updateServiceBookingStatus,
	uploadImage
}
